import React, { useEffect, useState, useCallback } from "react";
import "../HomePage/OurCategories.css";
import { API_URL } from "../../_services/apiUrl";
import { get } from "../../_services/apiService";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useWishlist } from "../../context/wishlistContext";

const categoriesCache = new Map();
const categoriesRequestCache = new Map();

const getHomeCategories = async (guestIdInt) => {
    const cacheKey = String(guestIdInt);

    if (categoriesCache.has(cacheKey)) {
        return categoriesCache.get(cacheKey);
    }

    if (categoriesRequestCache.has(cacheKey)) {
        return categoriesRequestCache.get(cacheKey);
    }

    const request = get(API_URL.categories, {
        guest_id: guestIdInt,
    })
        .then((response) => {
            let finalCategories = [];

            if (response?.status && Array.isArray(response.data)) {
                finalCategories = response.data;
            }

            categoriesCache.set(cacheKey, finalCategories);
            return finalCategories;
        })
        .finally(() => {
            categoriesRequestCache.delete(cacheKey);
        });

    categoriesRequestCache.set(cacheKey, request);
    return request;
};

const OurCategories = () => {
    const { guestIdInt } = useWishlist();
    const navigate = useNavigate();

    const [, setCategoriesLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [, setError] = useState(false);

    const fetchCategories = useCallback(async () => {
        if (!guestIdInt) {
            return;
        }

        try {
            setCategoriesLoading(true);
            setError(false);

            const finalCategories = await getHomeCategories(guestIdInt);

            // Optional: only home categories
            // const filteredCategories = finalCategories.filter(
            //     (item) => item.home_status === 1
            // );

            setCategories(finalCategories);
        } catch (fetchError) {
            setError(true);
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    }, [guestIdInt]);

    useEffect(() => {
        if (!guestIdInt) {
            setCategories([]);
            setCategoriesLoading(true);
            return;
        }

        fetchCategories();
    }, [fetchCategories, guestIdInt]);

    const onCategoryClick = (item) => {
        if (!item) return;

        const categoryId = item?.id;
        if (!categoryId) return;

        navigate(`/products/${categoryId}`);
    };

    const getCategoryImage = (category) => {
        return (
            category?.icon_full_url?.path ||
            category?.icon ||
            "/images/no_image.png"
        );
    };

    return (
        <section className="same_space_div">
            <div className="container">
                <div className="slider-container section_heading">
                    <h2>Our Best Categories</h2>

                    <div className="mb-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="210"
                            height="18"
                            viewBox="0 0 210 18"
                            fill="none"
                        >
                            <path
                                d="M85.4253 10.4814L0.952113 9.15095C0.902132 9.15964 0.850903 9.15792 0.80162 9.14589C0.752337 9.13386 0.706065 9.11176 0.665712 9.08102C0.62536 9.05027 0.591793 9.01153 0.567112 8.96721C0.542431 8.92289 0.527165 8.87394 0.522278 8.82345C0.527191 8.73276 0.565433 8.64708 0.629655 8.58286C0.693876 8.51864 0.779547 8.4804 0.870237 8.47549L85.3435 7.14502C85.7593 7.19087 86.1435 7.38855 86.4227 7.70014C86.7018 8.01174 86.8561 8.41536 86.8561 8.83369C86.8561 9.25202 86.7018 9.65564 86.4227 9.96724C86.1435 10.2788 85.7593 10.4765 85.3435 10.5223L85.4253 10.4814Z"
                                fill="url(#paint0_linear_167_36)"
                            ></path>
                            <path
                                d="M106.999 14.1453L105.096 17.6863L103.11 14.1862L99.3645 15.6599L100.101 11.7095L96.2738 10.4405L99.385 7.8819L97.2563 4.44322L101.289 4.48417L101.882 0.492799L104.952 3.11279L107.961 0.431396L108.616 4.42277L112.649 4.29993L110.602 7.7591L113.754 10.2767L109.967 11.6072L110.786 15.5576L106.999 14.1453Z"
                                fill="url(#paint1_linear_167_36)"
                            ></path>
                            <path
                                d="M91.8936 11.6483C93.4763 11.6483 94.7592 10.3653 94.7592 8.78268C94.7592 7.20007 93.4763 5.91711 91.8936 5.91711C90.311 5.91711 89.0281 7.20007 89.0281 8.78268C89.0281 10.3653 90.311 11.6483 91.8936 11.6483Z"
                                fill="url(#paint2_linear_167_36)"
                            ></path>
                            <path
                                d="M124.519 7.1046L208.972 8.43503C209.02 8.43495 209.067 8.44458 209.11 8.46333C209.154 8.48208 209.193 8.50959 209.226 8.5441C209.258 8.57861 209.283 8.61942 209.3 8.66403C209.316 8.70864 209.323 8.75609 209.32 8.80348C209.315 8.89417 209.277 8.97984 209.213 9.04406C209.148 9.10829 209.063 9.14653 208.972 9.15144L124.519 10.4819C124.282 10.508 124.042 10.4839 123.814 10.4109C123.587 10.338 123.377 10.218 123.2 10.0586C123.022 9.89931 122.879 9.70426 122.782 9.48622C122.685 9.26818 122.634 9.03205 122.634 8.79324C122.634 8.55443 122.685 8.31829 122.782 8.10025C122.879 7.88222 123.022 7.68717 123.2 7.52783C123.377 7.36849 123.587 7.24842 123.814 7.1755C124.042 7.10257 124.282 7.07843 124.519 7.1046Z"
                                fill="url(#paint3_linear_167_36)"
                            ></path>
                            <path
                                d="M118.032 11.6483C119.614 11.6483 120.897 10.3653 120.897 8.78268C120.897 7.20007 119.614 5.91711 118.032 5.91711C116.449 5.91711 115.166 7.20007 115.166 8.78268C115.166 10.3653 116.449 11.6483 118.032 11.6483Z"
                                fill="url(#paint4_linear_167_36)"
                            ></path>
                            <defs>
                                <linearGradient id="paint0_linear_167_36" x1="43.8744" y1="-9.78247" x2="43.8744" y2="15.2096" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#B27F2A"></stop>
                                    <stop offset="0.06" stopColor="#B7862E"></stop>
                                    <stop offset="0.14" stopColor="#C49939"></stop>
                                    <stop offset="0.24" stopColor="#DAB84C"></stop>
                                    <stop offset="0.33" stopColor="#F4DD62"></stop>
                                    <stop offset="0.52" stopColor="#F4E38B"></stop>
                                    <stop offset="0.66" stopColor="#F4DD62"></stop>
                                    <stop offset="1" stopColor="#B27F2A"></stop>
                                </linearGradient>
                                <linearGradient id="paint1_linear_167_36" x1="105.014" y1="-9.78236" x2="105.014" y2="15.2096" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#B27F2A"></stop>
                                    <stop offset="0.06" stopColor="#B7862E"></stop>
                                    <stop offset="0.14" stopColor="#C49939"></stop>
                                    <stop offset="0.24" stopColor="#DAB84C"></stop>
                                    <stop offset="0.33" stopColor="#F4DD62"></stop>
                                    <stop offset="0.52" stopColor="#F4E38B"></stop>
                                    <stop offset="0.66" stopColor="#F4DD62"></stop>
                                    <stop offset="1" stopColor="#B27F2A"></stop>
                                </linearGradient>
                                <linearGradient id="paint2_linear_167_36" x1="91.8936" y1="-9.78219" x2="91.8936" y2="15.2098" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#B27F2A"></stop>
                                    <stop offset="0.06" stopColor="#B7862E"></stop>
                                    <stop offset="0.14" stopColor="#C49939"></stop>
                                    <stop offset="0.24" stopColor="#DAB84C"></stop>
                                    <stop offset="0.33" stopColor="#F4DD62"></stop>
                                    <stop offset="0.52" stopColor="#F4E38B"></stop>
                                    <stop offset="0.66" stopColor="#F4DD62"></stop>
                                    <stop offset="1" stopColor="#B27F2A"></stop>
                                </linearGradient>
                                <linearGradient id="paint3_linear_167_36" x1="166.05" y1="-9.78181" x2="166.05" y2="15.2101" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#B27F2A"></stop>
                                    <stop offset="0.06" stopColor="#B7862E"></stop>
                                    <stop offset="0.14" stopColor="#C49939"></stop>
                                    <stop offset="0.24" stopColor="#DAB84C"></stop>
                                    <stop offset="0.33" stopColor="#F4DD62"></stop>
                                    <stop offset="0.52" stopColor="#F4E38B"></stop>
                                    <stop offset="0.66" stopColor="#F4DD62"></stop>
                                    <stop offset="1" stopColor="#B27F2A"></stop>
                                </linearGradient>
                                <linearGradient id="paint4_linear_167_36" x1="118.032" y1="-9.78219" x2="118.032" y2="15.2098" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#B27F2A"></stop>
                                    <stop offset="0.06" stopColor="#B7862E"></stop>
                                    <stop offset="0.14" stopColor="#C49939"></stop>
                                    <stop offset="0.24" stopColor="#DAB84C"></stop>
                                    <stop offset="0.33" stopColor="#F4DD62"></stop>
                                    <stop offset="0.52" stopColor="#F4E38B"></stop>
                                    <stop offset="0.66" stopColor="#F4DD62"></stop>
                                    <stop offset="1" stopColor="#B27F2A"></stop>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>

                    {categories && categories.length > 0 ? (
                        <Swiper
                            modules={[Navigation, Autoplay]}
                            spaceBetween={20}
                            slidesPerView={5}
                            navigation
                            autoplay={{
                                delay: 3000,
                                disableOnInteraction: false,
                            }}
                            key={categories.length}
                            breakpoints={{
                                320: { slidesPerView: 2, spaceBetween: 10 },
                                576: { slidesPerView: 2, spaceBetween: 15 },
                                768: { slidesPerView: 3, spaceBetween: 20 },
                                1024: { slidesPerView: 5, spaceBetween: 20 },
                            }}
                        >
                            {categories.map((item, index) => (
                                <SwiperSlide key={item?.id || index}>
                                    <div
                                        className="slider-item"
                                        onClick={() => onCategoryClick(item)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <img
                                            src={getCategoryImage(item)}
                                            alt={item.name || "category"}
                                            className="w-100"
                                            onError={(e) => {
                                                e.target.src = "/images/no_image.png";
                                            }}
                                        />
                                        <p className="slider-title">
                                            {item?.name || "Category"}
                                        </p>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div className="row justify-content-center">
                            {categories.map((item, index) => (
                                <div
                                    key={item?.id || index}
                                    className="slider-item single_category_item col-lg-3 col-md-4 col-6"
                                    onClick={() => onCategoryClick(item)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <img
                                        src={getCategoryImage(item)}
                                        alt={item.name || "category"}
                                        className="img-fluid"
                                        onError={(e) => {
                                            e.target.src = "/images/no_image.png";
                                        }}
                                    />
                                    <p className="slider-title">
                                        {item?.name || "Category"}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default OurCategories;
